// ActivityFeed.tsx
import React, { useEffect, useState } from "react";
import TextEditor from "components/TextEditor/TextEditor";
import { Button, Spinner } from "react-bootstrap";
import { MecordColors } from "Constants/Color";
import LoadingSpinner from "components/LoadingSpinner";
import DOMPurify from "dompurify";

// Props and Interface definitions (copied from parent)
interface Activity {
  id: string; // Activity ID
  user: string;
  date: string; // The schedule time or creation time
  text: string; // The comment/notes
  initials: string;
  bg: string;
  color: string;
  follow_up_type_name: string; // To display type (e.g., 'Call')
  assigned_to_detail?: string;
  attachments?: any[];
  is_editable?: boolean;
  source_entity_type?: string;
  source_module_label?: string;
}

interface ActivityFeedProps {
  loading?: boolean;
  activities: Activity[];
  onEdit?: (activity: Activity) => void;
  onDelete?: (id: string) => void;
  editingId?: string | null;
  onSave?: (id: string, content: string) => Promise<void>;
  onCancel?: () => void;
}

const ActivityFeed: React.FC<ActivityFeedProps> = ({
  loading,
  activities,
  onEdit,
  onDelete,
  editingId,
  onSave,
  onCancel,
}) => {
  // Ensure activities is an array and filter out any invalid entries
  // const validActivities = React.useMemo(
  //   () =>
  //     Array.isArray(activities)
  //       ? activities.filter((a: any) => {
  //           // Filter out error objects or invalid entries
  //           if (!a || typeof a !== "object") return false;
  //           if ("status" in a && "error" in a) return false; // Skip error objects
  //           return a.id; // Must have an id
  //         })
  //       : [],
  //   [activities]
  // );

const validActivities = React.useMemo(() => {
  if (!Array.isArray(activities)) return [];

  return activities
    .filter((a: any) => a && typeof a === "object" && a.id)
    .sort((a, b) => {
      // We store UTC ISO in DB; Date.parse will interpret it as UTC and convert correctly.
      // Sort oldest-first by timestamp while keeping invalid/missing dates at the end.
      const aTime = a?.date ? Date.parse(a.date) : NaN;
      const bTime = b?.date ? Date.parse(b.date) : NaN;

      const aValid = Number.isFinite(aTime);
      const bValid = Number.isFinite(bTime);

      if (!aValid && !bValid) return 0;
      if (!aValid) return 1; // a goes last
      if (!bValid) return -1; // b goes last

      return aTime - bTime;
    });
}, [activities]);

  const [editorContent, setEditorContent] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Initialize editor content when editing starts
  React.useEffect(() => {
    if (editingId) {
      const activity = validActivities.find((a) => a.id === editingId);
      if (activity) {
        setEditorContent(activity.text || "");
      }
    }
  }, [editingId, validActivities]);

  const handleSave = async (id: string) => {
    if (onSave) {
      setIsSaving(true);
      await onSave(id, editorContent);
      setIsSaving(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  // Get color scheme for follow-up type chip
  const getFollowUpTypeColors = (type: string) => {
    const normalizedType = type?.toLowerCase() || "";
    
    // Color mappings for different follow-up types
    const colorMap: Record<string, { bg: string; text: string; border: string }> = {
      call: {
        bg: "#e8f5e9",
        text: "#2e7d32",
        border: "#a5d6a7",
      },
      message: {
        bg: "#e3f2fd",
        text: "#1976d2",
        border: "#bbdefb",
      },
      email: {
        bg: "#fff3e0",
        text: "#e65100",
        border: "#ffcc80",
      },
      meeting: {
        bg: "#f3e5f5",
        text: "#7b1fa2",
        border: "#ce93d8",
      },
      "on site visit": {
        bg: "#fce4ec",
        text: "#c2185b",
        border: "#f48fb1",
      },
      "onsitevisit": {
        bg: "#fce4ec",
        text: "#c2185b",
        border: "#f48fb1",
      },
    };

    // Return matching color or default
    return (
      colorMap[normalizedType] || {
        bg: "#f5f5f5",
        text: "#616161",
        border: "#e0e0e0",
      }
    );
  };

  const getModuleChipColors = (moduleLabel: string) => {
    const key = (moduleLabel || "").toLowerCase();
    const colorMap: Record<string, { bg: string; text: string; border: string }> = {
      enquiry: {
        bg: "#e8f5e9",
        text: "#1b5e20",
        border: "#a5d6a7",
      },
      quotation: {
        bg: "#e3f2fd",
        text: "#0d47a1",
        border: "#90caf9",
      },
      "sales order": {
        bg: "#fff3e0",
        text: "#e65100",
        border: "#ffcc80",
      },
      payment: {
        bg: "#f3e5f5",
        text: "#6a1b9a",
        border: "#ce93d8",
      },
      "payment management": {
        bg: "#f3e5f5",
        text: "#6a1b9a",
        border: "#ce93d8",
      },
      other: {
        bg: "#f5f5f5",
        text: "#616161",
        border: "#e0e0e0",
      },
    };

    return colorMap[key] || colorMap.other;
  };

  return (
    <>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div>
          <div>
            {validActivities.length === 0 ? (
              <p className="text-muted text-center py-5">
                No activities logged yet.
              </p>
            ) : (
              validActivities.map((a: any) => {
                // Ensure all values are strings before rendering
                const user =
                  typeof a.user === "string"
                    ? a.user
                    : String(a.user || "Unknown User");
                const date =
                  typeof a.date === "string"
                    ? formatDate(a.date)
                    : String(a.date || "");
                const followUpType =
                  typeof a.follow_up_type_name === "string"
                    ? a.follow_up_type_name
                    : String(a.follow_up_type_name || "Initial Follow-up");
                const sourceModuleLabel =
                  typeof a.source_module_label === "string" &&
                  a.source_module_label.trim()
                    ? a.source_module_label
                    : "Other";
                const initials =
                  typeof a.initials === "string"
                    ? a.initials
                    : String(a.initials || "??");
                const text =
                  typeof a.text === "string"
                    ? a.text
                    : String(a.text || "No remark added");
                const bg = typeof a.bg === "string" ? a.bg : "#FF7F7F"; // Default to reddish if not provided
                const color = typeof a.color === "string" ? a.color : "#FFFFFF";
                const assignedToDetail = a.assigned_to_detail;
                const attachments = Array.isArray(a.attachments)
                  ? a.attachments
                  : [];
                const isEditable = a.is_editable;
                const isEditing = editingId === a.id;

                return (
                  <div key={a.id || Math.random()} className="mb-4">
                    {/* Header: Avatar + Name + Date */}
                    <div className="d-flex align-items-center mb-1">
                      <div
                        className="d-flex align-items-center justify-content-center rounded-circle me-3 flex-shrink-0"
                        style={{
                          width: "30px",
                          height: "30px",
                          background: bg,
                          color: color,
                          fontSize: "11px",
                          fontWeight: 600,
                        }}
                      >
                        {initials}
                      </div>
                      <div className="flex-grow-1">
                        <div
                          className="fw-bold text-dark"
                          style={{ fontSize: "13px", lineHeight: "1.2" }}
                        >
                          {user}
                        </div>
                        <div
                          className="d-flex align-items-center gap-2"
                          style={{ fontSize: "11px" }}
                        >
                          <span className="text-muted">{date}</span>
                          {followUpType && (() => {
                            const chipColors = getFollowUpTypeColors(followUpType);
                            return (
                              <span
                                className="px-2 py-1 rounded-pill fw-medium"
                                style={{
                                  fontSize: "10px",
                                  backgroundColor: chipColors.bg,
                                  color: chipColors.text,
                                  border: `1px solid ${chipColors.border}`,
                                }}
                              >
                                {followUpType}
                              </span>
                            );
                          })()}
                          {sourceModuleLabel && (() => {
                            const chipColors = getModuleChipColors(sourceModuleLabel);
                            return (
                              <span
                                className="px-2 py-1 rounded-pill fw-medium"
                                style={{
                                  fontSize: "10px",
                                  backgroundColor: chipColors.bg,
                                  color: chipColors.text,
                                  border: `1px solid ${chipColors.border}`,
                                }}
                              >
                                {sourceModuleLabel}
                              </span>
                            );
                          })()}
                        </div>
                      </div>
                    </div>

                    {/* Content Box */}
                    <div
                      className="ms-5 p-2"
                      style={{
                        border: `1px solid #dee2e6`,
                        background: "#FFFFFF",
                        fontSize: "13px",
                        position: "relative",
                        borderRadius: "12px",
                      }}
                    >
                      {isEditing ? (
                        <div>
                          <TextEditor
                            value={editorContent}
                            onChange={setEditorContent}
                            placeholder="Edit remarks..."
                            minHeightPx={80}
                            maxHeightPx={160}
                            blockImages={true}
                          />
                          <div className="d-flex gap-2 mt-2">
                            <Button
                              size="sm"
                              onClick={() => handleSave(a.id)}
                              disabled={isSaving}
                              style={{
                                backgroundColor:
                                  MecordColors.PrimaryButtonBGColor,
                                borderColor: MecordColors.PrimaryButtonBGColor,
                                color: "#fff",
                                height: "30px",
                                fontSize: "12px",
                                padding: "2px 12px",
                                transition: "all 0.2s ease",
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  MecordColors.PrimaryButtonHoverBGColor;
                                e.currentTarget.style.borderColor =
                                  MecordColors.PrimaryButtonHoverBGColor;
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  MecordColors.PrimaryButtonBGColor;
                                e.currentTarget.style.borderColor =
                                  MecordColors.PrimaryButtonBGColor;
                              }}
                            >
                              {isSaving ? (
                                <Spinner size="sm" animation="border" />
                              ) : (
                                "Save"
                              )}
                            </Button>
                            <Button
                              size="sm"
                              onClick={onCancel}
                              disabled={isSaving}
                              variant="light"
                              style={{
                                backgroundColor: "#f8f9fa",
                                borderColor: "#dee2e6",
                                color: "#495057",
                                height: "30px",
                                fontSize: "12px",
                                padding: "2px 12px",
                                transition: "all 0.2s ease",
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "#e9ecef";
                                e.currentTarget.style.borderColor = "#ced4da";
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "#f8f9fa";
                                e.currentTarget.style.borderColor = "#dee2e6";
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          {/* Main Text (rich HTML from TextEditor) */}
                          <div
                            className={
                              text ? "text-dark" : "text-muted fst-italic"
                            }
                            style={{
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                              opacity: text ? 0.9 : 0.7,
                            }}
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(text || "No remarks added"),
                            }}
                          />
                        </>
                      )}
                    </div>

                    {/* Actions: Edit / Delete */}
                    {isEditable && !isEditing && (
                      <div className="d-flex gap-3 mt-1 ms-1">
                        <span
                          role="button"
                          className="text-decoration-underline"
                          style={{
                            fontSize: "11px",
                            cursor: "pointer",
                            color: MecordColors.ActiveText,
                          }}
                          onClick={() => onEdit && onEdit(a)}
                        >
                          Edit
                        </span>
                        <span
                          role="button"
                          className="text-muted text-decoration-underline"
                          style={{ fontSize: "11px", cursor: "pointer" }}
                          onClick={() => onDelete && onDelete(a.id)}
                        >
                          Delete
                        </span>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ActivityFeed;
